import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { Client } from "../../models";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import TableSortLabel from "@mui/material/TableSortLabel";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

const ClientList: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState<Omit<Client, "id">>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    company: "",
  });
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof Client>("firstName");
  const user = auth.currentUser;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClients = async () => {
      if (!user) return;

      try {
        const q = query(
          collection(db, "clients"),
          where("userId", "==", user.uid)
        );
        const querySnapshot = await getDocs(q);
        const clientsData: Client[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Client[];
        setClients(clientsData);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };

    fetchClients();
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewClient((prevClient) => ({ ...prevClient, [name]: value }));
  };

  const handleAddClient = async () => {
    if (!user) return;

    if (!newClient.firstName || !newClient.lastName || !newClient.email) {
      alert("First Name, Last Name, and Email are required.");
      return;
    }

    try {
      // Add the new client and retrieve the document reference
      const docRef = await addDoc(collection(db, "clients"), {
        ...newClient,
        userId: user.uid,
      });

      // Update the newly created document to include the document ID
      await updateDoc(docRef, {
        id: docRef.id,
      });

      // Add the new client to the state
      setClients((prevClients) => [
        ...prevClients,
        {
          id: docRef.id,
          ...newClient,
          userId: user.uid,
        },
      ]);

      // Reset form and close the dialog
      setShowAddClient(false);
      setNewClient({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        company: "",
      });
    } catch (error) {
      console.error("Error adding client:", error);
    }
  };

  const handleRequestSort = (property: keyof Client) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedClients = clients.sort((a, b) => {
    const valA = a[orderBy] || "";
    const valB = b[orderBy] || "";
    return order === "asc"
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  });

  return (
    <div>
      {/* Add Client Dialog */}
      <Dialog
        open={showAddClient}
        onClose={() => setShowAddClient(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Client</DialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            name="firstName"
            value={newClient.firstName}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={newClient.lastName}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            value={newClient.email}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={newClient.phoneNumber}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Company"
            name="company"
            value={newClient.company}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddClient(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddClient} color="primary">
            Add Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table Section */}
      <TableContainer component={Paper} className="overflow-x-auto">
        <Table className="min-w-full">
          <TableHead className="sticky top-0 bg-gray-200 z-10">
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "firstName"}
                  direction={orderBy === "firstName" ? order : "asc"}
                  onClick={() => handleRequestSort("firstName")}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "lastName"}
                  direction={orderBy === "lastName" ? order : "asc"}
                  onClick={() => handleRequestSort("lastName")}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? order : "asc"}
                  onClick={() => handleRequestSort("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "phoneNumber"}
                  direction={orderBy === "phoneNumber" ? order : "asc"}
                  onClick={() => handleRequestSort("phoneNumber")}
                >
                  Phone Number
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "company"}
                  direction={orderBy === "company" ? order : "asc"}
                  onClick={() => handleRequestSort("company")}
                >
                  Company
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  No clients found. Add a client to get started.
                </TableCell>
              </TableRow>
            ) : (
              sortedClients.map((client, index) => (
                <TableRow
                  key={client.id}
                  onClick={() => navigate(`/clients/${client.id}`)}
                  className={`cursor-pointer odd:bg-gray-100 even:bg-white hover:bg-gray-200`}
                >
                  <TableCell>{client.firstName}</TableCell>
                  <TableCell>{client.lastName}</TableCell>
                  <TableCell>{client.email}</TableCell>
                  <TableCell>{client.phoneNumber || "N/A"}</TableCell>
                  <TableCell>{client.company}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Client Button */}
      <Box m={2} className="flex justify-center">
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowAddClient(true)}
          className="px-6 py-2"
        >
          Add Client
        </Button>
      </Box>
    </div>
  );
};

export default ClientList;
